import { Clear, FilterAlt } from '@mui/icons-material'
import {
  Autocomplete,
  type AutocompleteRenderInputParams,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  OutlinedInput,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type { TemplateListItem } from '@ulysses-inc/harami_api_client'
import { PlaceNode } from '@ulysses-inc/harami_api_client'
import React, { useEffect, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { useDebounce } from 'src/exShared/hooks/useDebounce'
import { usePlaceLoadingStatus } from 'src/exShared/hooks/usePlaceLoadingStatus'
import {
  initializePlaceFilter,
  splitAccessiblePlaceNode,
} from 'src/exShared/libraries/filterDropDown/place'
import { mergedPlaceNodeIds } from 'src/exShared/util/place/placeNode'
import { splitLoginPlaceUUIDs } from 'src/exShared/util/place/splitLoginPlace'
import { flattenNodes } from 'src/features/places/EditPlaceGroup'
import {
  SchedulesFilter,
  schedulesListFilter,
} from 'src/features/schedules/schedulesListFilter'
import { GetSchedulesFilter } from 'src/state/ducks/schedules/types'
import { RootState } from 'src/state/store'

interface OwnProps {
  getSchedules: (filter?: GetSchedulesFilter) => void
}

const SchedulesStatusOptions: { label: string; value: number }[] = [
  { label: 'アクティブ', value: 1 },
  { label: '停止中', value: 0 },
]

const SchedulesFilterDropDown: React.FC<OwnProps> = (props: OwnProps) => {
  const request = useSelector(
    (state: RootState) => state.schedulesState.schedules.request,
  )
  const templates = useSelector(
    (state: RootState) => state.schedulesState.schedulesTemplates.templates,
  )
  const places = useSelector(
    (state: RootState) => state.placesState.places.places,
    shallowEqual,
  )
  const placeNodes = useSelector(
    (state: RootState) => flattenNodes(state.placesState.placeGroups.nodes, []),
    shallowEqual,
  )

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const isOpen = anchorEl !== null
  const [scheduleName, setScheduleName] = useState<string>('')
  const [templateIds, setTemplateIds] = useState<number[]>([])
  const [statuses, setStatuses] = useState<number[]>([])
  const [placeNodeIds, setPlaceNodeIds] = useState<string[]>([])
  const [placeGroupNodeIds, setPlaceGroupNodeIds] = useState<string[]>([])
  const filters = schedulesListFilter.get()
  const schedulesRequest = filters?.filter
  const placeLoadingStatus = usePlaceLoadingStatus()

  const { loginPlaceGroupIds, loginPlaceIds } = splitLoginPlaceUUIDs()

  useEffect(() => {
    if (placeLoadingStatus !== 'finish') {
      return
    }
    if (!schedulesRequest) {
      const placeFilter = initializePlaceFilter(placeNodes)
      if (placeFilter === undefined || placeFilter.$in.length === 0) {
        props.getSchedules()
        // eslint-disable-next-line react-hooks/set-state-in-effect -- useEffect内でのsetState呼び出しは既存設計のため、リファクタリング時に修正する
        setPlaceNodeIds(loginPlaceIds)
        setPlaceGroupNodeIds(loginPlaceGroupIds)
        return
      }
      const filter = { placeNodeId: placeFilter }
      props.getSchedules(filter)
      setPlaceNodeIds(loginPlaceIds)
      setPlaceGroupNodeIds(loginPlaceGroupIds)
      return
    }

    let filterPlaceNodeIds: string[] = []
    let filterPlaceGroupNodeIds: string[] = []
    if (
      (schedulesRequest?.placeNodeIds ?? []).length > 0 ||
      (schedulesRequest?.placeGroupNodeIds ?? []).length > 0
    ) {
      filterPlaceNodeIds = schedulesRequest?.placeNodeIds ?? []
      filterPlaceGroupNodeIds = schedulesRequest?.placeGroupNodeIds ?? []
    } else {
      filterPlaceNodeIds = loginPlaceIds
      filterPlaceGroupNodeIds = loginPlaceGroupIds
    }
    setPlaceNodeIds(filterPlaceNodeIds)
    setPlaceGroupNodeIds(filterPlaceGroupNodeIds)
    setScheduleName(schedulesRequest?.scheduleName ?? '')
    setTemplateIds(schedulesRequest?.templateIds ?? [])
    setStatuses(schedulesRequest?.statuses ?? [])
    // eslint-disable-next-line react-hooks/immutability -- 関数の宣言順序による既存パターンのため、リファクタリング時に修正する
    const filter = getFilter({
      scheduleName: schedulesRequest?.scheduleName,
      templateIds: schedulesRequest?.templateIds,
      placeNodeIds: filterPlaceNodeIds,
      placeGroupNodeIds: filterPlaceGroupNodeIds,
      statuses: schedulesRequest?.statuses,
    })
    props.getSchedules(filter)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeLoadingStatus])

  const isFilterOn = () => {
    return (
      !!scheduleName ||
      templateIds.length > 0 ||
      statuses.length > 0 ||
      placeGroupNodeIds.length > 0 ||
      placeNodeIds.length > 0
    )
  }

  const clearFilter = () => {
    const conditions = {
      placeNodeIds: loginPlaceIds,
      placeGroupNodeIds: loginPlaceGroupIds,
    }
    const filter = getFilter(conditions)
    props.getSchedules(filter)
    setScheduleName('')
    setTemplateIds([])
    setStatuses([])
    setPlaceNodeIds([])
    setPlaceGroupNodeIds([])
    saveFilter(conditions)
  }

  const saveFilter = (conditions: SchedulesFilter) => {
    schedulesListFilter.set({
      ...filters,
      pagination: request,
      filter: conditions,
    })
  }

  const getFilter = (conditions: SchedulesFilter): GetSchedulesFilter => {
    const allPlaceNodeIds = mergedPlaceNodeIds(
      placeNodes,
      conditions.placeNodeIds ?? [],
      conditions.placeGroupNodeIds ?? [],
    )
    const scheduleNameCondition =
      conditions.scheduleName !== undefined &&
      conditions.scheduleName.length > 0
        ? { $like: conditions.scheduleName }
        : undefined
    const templateIdCondition =
      conditions.templateIds && conditions.templateIds.length > 0
        ? { $in: conditions.templateIds }
        : undefined
    const placeNodeIdCondition =
      allPlaceNodeIds && allPlaceNodeIds.length > 0
        ? { $in: allPlaceNodeIds }
        : undefined
    const statusesCondition =
      conditions.statuses && conditions.statuses.length > 0
        ? { $in: conditions.statuses }
        : undefined
    saveFilter(conditions)
    return {
      scheduleName: scheduleNameCondition,
      templateId: templateIdCondition,
      placeNodeId: placeNodeIdCondition,
      status: statusesCondition,
    }
  }

  const accessiblePlaceNode = splitAccessiblePlaceNode(placeNodes, places)

  const debounce = useDebounce(300)
  const handleScheduleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setScheduleName(e.target.value)
    debounce(() => {
      const filterPlaceNodeIds =
        placeNodeIds.length > 0 || placeGroupNodeIds.length > 0
          ? placeNodeIds
          : loginPlaceIds
      const filterPlaceGroupNodeIds =
        placeNodeIds.length > 0 || placeGroupNodeIds.length > 0
          ? placeGroupNodeIds
          : loginPlaceGroupIds
      const filter = getFilter({
        scheduleName: e.target.value,
        templateIds,
        statuses,
        placeNodeIds: filterPlaceNodeIds,
        placeGroupNodeIds: filterPlaceGroupNodeIds,
      })
      props.getSchedules(filter)
    })
  }

  const groundArea = (
    <Button
      color={isFilterOn() ? 'filtered' : 'secondary'}
      variant="outlined"
      startIcon={<FilterAlt />}
      onClick={({ currentTarget }) => {
        setAnchorEl(currentTarget)
      }}
    >
      絞り込み: {isFilterOn() ? 'ON' : 'OFF'}
    </Button>
  )

  const hoverArea = (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-end">
        <Button onClick={clearFilter}>クリア</Button>
      </Stack>
      <Stack>
        <Typography variant="subtitle1">スケジュール名</Typography>
        <OutlinedInput
          fullWidth
          size="small"
          onChange={handleScheduleNameChange}
          value={scheduleName}
          endAdornment={
            scheduleName && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => {
                    setScheduleName('')
                    const filterPlaceNodeIds =
                      placeNodeIds.length > 0 || placeGroupNodeIds.length > 0
                        ? placeNodeIds
                        : loginPlaceIds
                    const filterPlaceGroupNodeIds =
                      placeNodeIds.length > 0 || placeGroupNodeIds.length > 0
                        ? placeGroupNodeIds
                        : loginPlaceGroupIds
                    const filter = getFilter({
                      scheduleName: '',
                      templateIds,
                      statuses: statuses,
                      placeNodeIds: filterPlaceNodeIds,
                      placeGroupNodeIds: filterPlaceGroupNodeIds,
                    })
                    props.getSchedules(filter)
                  }}
                  edge="end"
                >
                  <Clear fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }
        />
      </Stack>
      <Stack>
        <Typography variant="subtitle1">ひな形名</Typography>
        <Autocomplete<TemplateListItem, true, false, false>
          multiple
          size="small"
          disableCloseOnSelect
          options={templates}
          getOptionKey={option => String(option.id)}
          getOptionLabel={option => option.name ?? ''}
          value={templateIds
            .map(uuid => templates.find(template => template.id === uuid))
            .filter(
              (template): template is TemplateListItem =>
                template !== undefined,
            )}
          onChange={(_, newValue) => {
            const newTemplateIds = newValue.map(template => template.id || 0)
            setTemplateIds(newTemplateIds || [])
            const filterPlaceNodeIds =
              placeNodeIds.length > 0 || placeGroupNodeIds.length > 0
                ? placeNodeIds
                : loginPlaceIds
            const filterPlaceGroupNodeIds =
              placeNodeIds.length > 0 || placeGroupNodeIds.length > 0
                ? placeGroupNodeIds
                : loginPlaceGroupIds
            const filter: GetSchedulesFilter = getFilter({
              scheduleName,
              templateIds: newTemplateIds || [],
              statuses,
              placeNodeIds: filterPlaceNodeIds,
              placeGroupNodeIds: filterPlaceGroupNodeIds,
            })
            props.getSchedules(filter)
          }}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Stack direction="row" alignItems="center" spacing={1} mr={1}>
                <Checkbox size="small" checked={selected} />
                {option.name}
              </Stack>
            </li>
          )}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField {...params} />
          )}
        />
      </Stack>
      <Stack>
        <Typography variant="subtitle1">ステータス</Typography>
        <Stack direction="row" spacing={2}>
          {SchedulesStatusOptions.map(option => (
            <FormControlLabel
              key={option.value}
              control={
                <Checkbox
                  checked={statuses.includes(option.value)}
                  onChange={event => {
                    const checked = event.target.checked
                    const value = option.value
                    const nextStatuses = checked
                      ? [...statuses, value]
                      : statuses.filter(s => s !== value)
                    setStatuses(nextStatuses)

                    const filterPlaceNodeIds =
                      placeNodeIds.length > 0 || placeGroupNodeIds.length > 0
                        ? placeNodeIds
                        : loginPlaceIds
                    const filterPlaceGroupNodeIds =
                      placeNodeIds.length > 0 || placeGroupNodeIds.length > 0
                        ? placeGroupNodeIds
                        : loginPlaceGroupIds

                    const filter = getFilter({
                      scheduleName,
                      templateIds,
                      statuses: nextStatuses,
                      placeNodeIds: filterPlaceNodeIds,
                      placeGroupNodeIds: filterPlaceGroupNodeIds,
                    })

                    props.getSchedules(filter)
                  }}
                />
              }
              label={option.label}
            />
          ))}
        </Stack>
      </Stack>
      <Stack>
        <Typography variant="subtitle1">現場グループ名</Typography>
        <Autocomplete<PlaceNode, true, false, false>
          multiple
          size="small"
          disableCloseOnSelect
          options={accessiblePlaceNode.accessiblePlaceGroups}
          getOptionLabel={option => option.placeGroup?.name ?? '名称未設定'}
          value={placeGroupNodeIds
            .map(uuid =>
              accessiblePlaceNode.accessiblePlaceGroups.find(
                placeNode => placeNode.uuid === uuid,
              ),
            )
            .filter(
              (placeNode): placeNode is PlaceNode => placeNode !== undefined,
            )}
          onChange={(_, newValue) => {
            const newPlaceGroupNodeIds = newValue.map(
              placeNode => placeNode.uuid,
            )
            setPlaceGroupNodeIds(newPlaceGroupNodeIds || [])
            const filterPlaceNodeIds =
              placeNodeIds.length > 0 || (newPlaceGroupNodeIds ?? []).length > 0
                ? placeNodeIds
                : loginPlaceIds
            const filterPlaceGroupNodeIds =
              placeNodeIds.length > 0 || (newPlaceGroupNodeIds ?? []).length > 0
                ? newPlaceGroupNodeIds
                : loginPlaceGroupIds
            const filter = getFilter({
              scheduleName,
              templateIds,
              statuses,
              placeNodeIds: filterPlaceNodeIds,
              placeGroupNodeIds: filterPlaceGroupNodeIds || [],
            })
            props.getSchedules(filter)
          }}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Stack direction="row" alignItems="center" spacing={1} mr={1}>
                <Checkbox size="small" checked={selected} />
                {option.placeGroup?.name}
              </Stack>
            </li>
          )}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField {...params} />
          )}
        />
      </Stack>
      <Stack>
        <Typography variant="subtitle1">現場名</Typography>
        <Autocomplete<PlaceNode, true, false, false>
          multiple
          size="small"
          disableCloseOnSelect
          options={accessiblePlaceNode.accessiblePlaces}
          getOptionLabel={option => option.place?.name ?? '名称未設定'}
          value={placeNodeIds
            .map(uuid =>
              accessiblePlaceNode.accessiblePlaces.find(
                placeNode => placeNode.uuid === uuid,
              ),
            )
            .filter(
              (placeNode): placeNode is PlaceNode => placeNode !== undefined,
            )}
          onChange={(_, newValue) => {
            const newPlaceNodeIds = newValue.map(placeNode => placeNode.uuid)
            setPlaceNodeIds(newPlaceNodeIds)
            const filterPlaceNodeIds =
              (newPlaceNodeIds ?? []).length > 0 || placeGroupNodeIds.length > 0
                ? newPlaceNodeIds
                : loginPlaceIds
            const filterPlaceGroupNodeIds =
              (newPlaceNodeIds ?? []).length > 0 || placeGroupNodeIds.length > 0
                ? placeGroupNodeIds
                : loginPlaceGroupIds
            const filter = getFilter({
              scheduleName,
              templateIds,
              statuses,
              placeNodeIds: filterPlaceNodeIds || [],
              placeGroupNodeIds: filterPlaceGroupNodeIds || [],
            })
            props.getSchedules(filter)
          }}
          isOptionEqualToValue={(o, v) => o.id === v.id}
          renderOption={(props, option, { selected }) => (
            <li {...props}>
              <Stack direction="row" alignItems="center" spacing={1} mr={1}>
                <Checkbox size="small" checked={selected} />
                {option.place?.name}
              </Stack>
            </li>
          )}
          renderInput={(params: AutocompleteRenderInputParams) => (
            <TextField {...params} />
          )}
        />
      </Stack>
    </Stack>
  )

  return (
    <Box>
      {groundArea}
      <Popover
        id="grid-schedules-filter-popover"
        anchorEl={anchorEl}
        open={isOpen}
        onClose={() => {
          setAnchorEl(null)
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              padding: 2,
              width: '100%',
              maxWidth: 400,
            },
          },
        }}
      >
        {hoverArea}
      </Popover>
    </Box>
  )
}

export default SchedulesFilterDropDown
